import React from 'react'
import styles from 'styles/components/SectionHeader.module.sass'

/**
 * Props for the SectionHeader component.
 * 
 * @interface SectionHeaderProps
 * @property {string} [title] - The main title of the section (optional).
 * @property {string} [subtitle] - The subtitle of the section (optional).
 * @property {('light' | 'dark')} [theme='light'] - The theme for the section header, default is 'light'.
 */
interface SectionHeaderProps {
    title?: string;
    subtitle?: string;
    theme?: 'light' | 'dark';
}

/**
 * SectionHeader component renders a section title and optional subtitle 
 * in a styled wrapper. Primarily used for visually distinct section headers.
 *
 * @component
 * @param {SectionHeaderProps} props - The properties for the SectionHeader component.
 * @param {string} [props.title] - The main title of the section (optional).
 * @param {string} [props.subtitle] - The subtitle of the section (optional).
 * @param {('light' | 'dark')} [props.theme='light'] - The theme for the section header, default is 'light'.
 *
 * @example
 * <SectionHeader title="Our Services" subtitle="What we offer" />
 *
 * @returns {JSX.Element} The JSX for the SectionHeader component.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle, theme = 'light' }) => {
    return (
        <div className={styles['title-wrapper']}>
            {title && (
                <h2 
                    className={styles['section-title']}
                    style={{ color: theme === 'dark' ? '#ffffff' : '#707070'}}
                >
                    {title}
                </h2>
            )}
            {subtitle && (
                <h2 
                    className={styles['section-subtitle']}
                    style={{ color: theme === 'dark' ? '#ffffff' : '#005581'}}
                >
                    {subtitle}
                </h2>
            )}
        </div>
    )
}

export default SectionHeader;